import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Loader2, MessageSquare } from "lucide-react";
import pkg from "../../../package.json";
import { api } from "@/lib/api";
import { safeStorage } from "@/lib/utils";
import { toast } from "sonner";

export function FeedbackModal() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState<"bug" | "feature_request" | "general">("bug");
  const [message, setMessage] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    const lastFeedback = safeStorage.getItem("lastFeedback");
    if (lastFeedback) {
      const elapsed = Math.floor((Date.now() - parseInt(lastFeedback, 10)) / 1000);
      if (elapsed < 60) {
        setCooldown(60 - elapsed);
      }
    }
  }, []);

  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  const handleSubmit = async () => {
    if (cooldown > 0) return;
    if (!message.trim()) {
      toast.error("Please enter a message");
      return;
    }

    setLoading(true);
    try {
      await api.submitFeedback({
        type,
        message: message.trim(),
        appVersion: `v${pkg.version}${pkg.version.includes("-") ? "" : " (Beta)"}`,
      });

      toast.success("Feedback submitted! Thank you.");
      setOpen(false);
      setMessage("");
      setCooldown(60);
      safeStorage.setItem("lastFeedback", Date.now().toString());
    } catch (error) {
      toast.error("Failed to submit feedback. Please try again.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full justify-start">
          <MessageSquare className="mr-2 h-4 w-4" />
          Submit Feedback / Report Bug
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Submit Feedback</DialogTitle>
          <DialogDescription>Help us improve the beta! Report bugs or suggest new features.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="feedback-type">Type of Feedback</Label>
            <Select value={type} onValueChange={(val: any) => setType(val)}>
              <SelectTrigger id="feedback-type">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bug">Report a Bug</SelectItem>
                <SelectItem value="feature_request">Suggest a Feature</SelectItem>
                <SelectItem value="general">General Feedback</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              placeholder={
                type === "bug" ? "What happened? How can we reproduce it?" : "Explain your idea or feedback..."
              }
              className="resize-none h-32"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading || !message.trim() || cooldown > 0}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {cooldown > 0 ? `Wait ${cooldown}s` : "Submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
